/**
 * Desktop-only: turn the Electron shell's WASAPI loopback PCM stream into a
 * WebRTC MediaStreamTrack that the SFU send-path can produce as `screen-audio`.
 *
 * Two shell capture modes share this pipeline:
 *   - 'app'    — process-loopback of the SHARED window's process tree only
 *                (no call echo by construction);
 *   - 'system' — all PC audio EXCEPT Bunker's own process tree (shells
 *                ≥1.0.30): whole-screen shares and the window fallback, still
 *                without re-capturing the call.
 *
 * Flow: main process captures via the loopback addon → streams raw Int16 PCM
 * over IPC → here we feed it into an AudioWorklet ring buffer →
 * MediaStreamDestination → audio track.
 *
 * The addon's WAVEFORMATEX is hardcoded to 48000 Hz / 16-bit / stereo
 * (verified against its C++ source and by a tone measurement).
 */
// Real same-origin worklet asset (Vite emits /assets/screenPcmProcessor-*.js).
//
// It used to be a Blob URL, which the app's CSP (`script-src 'self'
// 'wasm-unsafe-eval'`) blocks: `addModule()` rejected, the caller tore the
// freshly started shell capture down ~20 ms later, and every screen share went
// out silent — the «друг не слышит демку» bug, visible in the desktop log as
// `capturing pid … (app)` immediately followed by `stopped pid …`. The mic
// filter already ships its processor this way for the same reason.
import screenPcmWorkletUrl from './screenPcmProcessor.js?url';

const PCM_RATE = 48000;
const CHANNELS = 2;

/**
 * Why the last screen-audio capture attempt failed, for the sharer-facing
 * status and the voice debug snapshot. Every failure path here returns `null`
 * (callers treat that as "try the next strategy"), so without this the reason
 * was thrown away — which is precisely why a CSP-blocked worklet looked
 * identical to "this platform has no screen audio" for weeks.
 */
let lastScreenAudioFailure: string | null = null;

export function getLastScreenAudioFailure(): string | null {
  return lastScreenAudioFailure;
}

export interface ScreenAudioCapture {
  track: MediaStreamTrack;
  stop: () => void;
  /** Which shell capture produced this track. 'app' = shared window's process
   *  only; 'system' = all PC audio minus Bunker; 'system-mix' = the complete
   *  output mix (Linux PipeWire/PulseAudio). */
  mode: 'app' | 'system' | 'system-mix';
  /** Live PCM counters — lets the caller detect a capture that technically
   *  started but never delivers sound (wrong pid, anti-cheat, silent app)
   *  and fall back. `nonSilentChunks` counts chunks with ≥1 non-zero sample. */
  stats: () => { chunks: number; nonSilentChunks: number };
}

type StartResult = { pid: string | number | null; mode?: 'app' | 'system' | 'system-mix' | null };

/** Wire a started shell capture into a MediaStreamTrack. Shared by the
 *  per-window and system paths; returns null (and stops the shell capture)
 *  when the local audio graph can't be built. */
async function buildTrackFromBridge(
  bridge: NonNullable<Window['bunkerDesktop']>,
  mode: 'app' | 'system' | 'system-mix',
): Promise<ScreenAudioCapture | null> {
  const Ctor: typeof AudioContext =
    window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctor({ sampleRate: PCM_RATE });

  const cleanup = (stopBridge: boolean) => {
    if (stopBridge) {
      try {
        void bridge.stopScreenAudio?.();
      } catch {
        /* ignore */
      }
    }
    void ctx.close().catch(() => {});
  };

  try {
    await ctx.audioWorklet.addModule(screenPcmWorkletUrl);
  } catch (err) {
    // Record WHY. This path silently returned null for weeks; the reason is
    // now visible to the sharer (and in the voice debug snapshot) instead of
    // being indistinguishable from "no audio available on this platform".
    lastScreenAudioFailure = err instanceof Error ? err.message : 'worklet_failed';
    cleanup(true);
    return null;
  }

  const node = new AudioWorkletNode(ctx, 'screen-pcm-player', {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [CHANNELS],
  });
  const dest = ctx.createMediaStreamDestination();
  node.connect(dest);

  let chunks = 0;
  let nonSilentChunks = 0;
  const off = bridge.onScreenAudioPcm!((chunk) => {
    chunks++;
    // Cheap silence probe: scan as 16-bit words for any non-zero sample.
    const i16 = new Int16Array(chunk.buffer, chunk.byteOffset, chunk.byteLength >> 1);
    for (let i = 0; i < i16.length; i++) {
      if (i16[i] !== 0) {
        nonSilentChunks++;
        break;
      }
    }
    // chunk is a Uint8Array view over the IPC payload — copy to a standalone
    // ArrayBuffer we can transfer to the worklet thread without detaching it.
    const ab = chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength);
    node.port.postMessage(ab, [ab]);
  });

  const track = dest.stream.getAudioTracks()[0] ?? null;
  if (!track) {
    lastScreenAudioFailure = 'no_track_from_destination';
    off();
    try {
      node.disconnect();
    } catch {
      /* detached */
    }
    cleanup(true);
    return null;
  }

  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    off();
    try {
      node.disconnect();
    } catch {
      /* detached */
    }
    cleanup(true);
  };

  lastScreenAudioFailure = null;
  return { track, stop, mode, stats: () => ({ chunks, nonSilentChunks }) };
}

function bridgeWithAudio(): NonNullable<Window['bunkerDesktop']> | null {
  const bridge = window.bunkerDesktop;
  if (!bridge?.startScreenAudio || !bridge.onScreenAudioPcm || !bridge.stopScreenAudio) {
    return null;
  }
  return bridge;
}

/**
 * Start screen audio for the given desktopCapturer source. Window sources
 * capture that app's process tree; whole-screen sources capture the system
 * mix minus Bunker (shells ≥1.0.30). Returns a track + stop handle, or null
 * when unavailable (not desktop, old shell, or capture failed) — callers
 * treat null as "no audio yet, try the next strategy".
 */
export async function startScreenAudioTrack(sourceId: string): Promise<ScreenAudioCapture | null> {
  const bridge = bridgeWithAudio();
  if (!bridge) return null;

  let started: StartResult;
  try {
    started = await bridge.startScreenAudio!(sourceId);
  } catch (err) {
    lastScreenAudioFailure = err instanceof Error ? err.message : 'shell_start_failed';
    return null;
  }
  if (started?.pid == null) {
    lastScreenAudioFailure = 'no_process_for_window';
    try {
      await bridge.stopScreenAudio!();
    } catch {
      /* nothing was running */
    }
    return null;
  }
  // Old shells don't report a mode — they only ever captured window processes.
  return buildTrackFromBridge(bridge, started.mode ?? 'app');
}

/**
 * Start the system capture directly: all PC audio EXCEPT Bunker's own process
 * tree. Used for whole-screen shares and as the fallback when a window's own
 * process can't be captured. Null on old shells (<1.0.30) / failure.
 */
export async function startSystemScreenAudioTrack(): Promise<ScreenAudioCapture | null> {
  const bridge = bridgeWithAudio();
  if (!bridge?.startSystemScreenAudio) return null;

  let started: StartResult;
  try {
    started = await bridge.startSystemScreenAudio();
  } catch (err) {
    lastScreenAudioFailure = err instanceof Error ? err.message : 'shell_start_failed';
    return null;
  }
  if (started?.pid == null) {
    lastScreenAudioFailure = 'shell_system_capture_unavailable';
    try {
      await bridge.stopScreenAudio!();
    } catch {
      /* nothing was running */
    }
    return null;
  }
  return buildTrackFromBridge(bridge, started.mode ?? 'system');
}
